/**
 * Big Screen Schema — Catalog of the 5 display screens and every visual
 * element that appears on each of them.
 *
 * Screens (mapped to a runtime state in DisplayPage / TimerActiveDisplay):
 *   circle_start  → Pre-countdown for a circle ("Get ready! 3-2-1")
 *   circle_run    → Circle timer running (big number + corner athletes)
 *   circle_vote   → Circle intro / voting screen (position cards + judges)
 *   battle_run    → Battle timer running (2 side athletes + timer + round)
 *   battle_vote   → Battle intro / voting (TOP32 title + 2 vs + judges)
 *
 * Each element declares:
 *   key    stable identifier (screen-scoped, e.g. "athlete_name")
 *   label  human-readable label
 *   preview  small preview text ("LAURA")
 *   kind   what fields to expose:
 *            "text"       → color + size + offset_x + offset_y
 *            "text_bg"    → text + bg_color
 *            "photo"      → size + bg_color + border_color + offset_x/y
 *            "highlight"  → color + glow (intensity 0..1)
 *            "flag"       → size only (as % of parent photo) + offset_x/y
 *            "number"     → color + size + offset_x/y (alias of text)
 *   sizeRange  [min, max] for the size slider (defaults 0.3–3)
 */

export const SCREENS = [
  {
    id: "circle_start",
    label: "Circle Start (Get ready!)",
    description: "The 'Get ready! 3-2-1' countdown before a circle round.",
    elements: [
      { key: "get_ready_text",       label: "'Get ready!' text",       preview: "Get ready!", kind: "text" },
      { key: "pre_countdown_number", label: "Countdown 3-2-1",         preview: "3",           kind: "text", sizeRange: [0.5, 5] },
    ],
  },
  {
    id: "circle_run",
    label: "Circle Run (timer running)",
    description: "Circle timer running with the big number and the 3–4 corner athletes.",
    elements: [
      { key: "circle_title",         label: "Circle title",            preview: "CIRCLE 2", kind: "text" },
      { key: "round_label",          label: "Round label",             preview: "ROUND 1",  kind: "text" },
      { key: "timer_number",         label: "Timer big number",        preview: "26",       kind: "text", sizeRange: [0.5, 5] },
      { key: "corner_card_bg",       label: "Corner card background",  preview: null,       kind: "photo" },
      { key: "corner_photo",         label: "Corner athlete photo (fallback initial)", preview: "R", kind: "photo" },
      { key: "corner_name",          label: "Corner athlete name",     preview: "RAYAN",    kind: "text" },
      { key: "corner_country",       label: "Corner athlete country",  preview: "FRANCE",   kind: "text" },
      { key: "corner_position",      label: "Corner position label (P1..P4)", preview: "P1", kind: "text" },
      { key: "corner_flag",          label: "Corner flag badge",       preview: null,       kind: "flag" },
      { key: "active_highlight",     label: "Active athlete highlight (color + halo)", preview: null, kind: "highlight" },
    ],
  },
  {
    id: "circle_vote",
    label: "Circle Judges Vote (setup / voting)",
    description: "Circle intro with position cards and the judges panel below.",
    elements: [
      { key: "circle_title",         label: "Circle title",              preview: "CIRCLE 2",    kind: "text" },
      { key: "position_label",       label: "Position label (POSITION 1..4)", preview: "POSITION 1", kind: "text" },
      { key: "athlete_photo",        label: "Athlete photo (fallback initial)", preview: "R", kind: "photo" },
      { key: "athlete_flag",         label: "Athlete flag badge",        preview: null,          kind: "flag" },
      { key: "athlete_name",         label: "Athlete name",              preview: "RAYAN",       kind: "text" },
      { key: "athlete_wr",           label: "Athlete world ranking",     preview: "#140",        kind: "text" },
      { key: "athlete_score",        label: "Athlete score",             preview: "0",           kind: "text" },
      { key: "card_background",      label: "Position card background",  preview: null,          kind: "photo" },
      { key: "judges_panel_bg",      label: "Judges panel background",   preview: null,          kind: "photo" },
      { key: "judge_dot",            label: "Judge dot indicator",       preview: null,          kind: "photo" },
      { key: "judge_name",           label: "Judge name",                preview: "BOYKA",       kind: "text" },
      { key: "judge_status",         label: "Judge status",              preview: "Waiting",     kind: "text" },
    ],
  },
  {
    id: "battle_run",
    label: "Battle Run (timer running)",
    description: "Battle timer running with the round, the big number and two side athletes.",
    elements: [
      { key: "round_label",          label: "Round label",              preview: "ROUND 1",     kind: "text" },
      { key: "timer_number",         label: "Timer big number",         preview: "30",          kind: "text", sizeRange: [0.5, 5] },
      { key: "side_photo",           label: "Side athlete photo (fallback initial)", preview: "J", kind: "photo" },
      { key: "side_flag",            label: "Side athlete flag badge",  preview: null,          kind: "flag" },
      { key: "side_name",            label: "Side athlete name",        preview: "JULIEN",      kind: "text" },
      { key: "side_country",         label: "Side athlete country",     preview: "SWITZERLAND", kind: "text" },
      { key: "side_wr",              label: "Side athlete WR",          preview: "#128",        kind: "text" },
      { key: "active_highlight",     label: "Active athlete highlight (color + halo)", preview: null, kind: "highlight" },
    ],
  },
  {
    id: "battle_vote",
    label: "Battle Judges Vote (setup / voting)",
    description: "Battle intro with the TOP xx title, two athletes side by side and the judges panel below.",
    elements: [
      { key: "battle_title",         label: "Battle title (TOP 32 / etc)", preview: "TOP 32",   kind: "text" },
      { key: "athlete_photo",        label: "Athlete photo (fallback initial)", preview: "J", kind: "photo" },
      { key: "athlete_flag",         label: "Athlete flag badge",       preview: null,          kind: "flag" },
      { key: "athlete_name",         label: "Athlete name",             preview: "JULIEN",      kind: "text" },
      { key: "athlete_wr",           label: "Athlete WR",               preview: "#128",        kind: "text" },
      { key: "vs_label",             label: "VS separator",             preview: "VS",          kind: "text" },
      { key: "card_background",      label: "Athlete card background",  preview: null,          kind: "photo" },
      { key: "athlete1_color",       label: "Athlete 1 color (left side / violet by default)", preview: "20", kind: "highlight" },
      { key: "athlete2_color",       label: "Athlete 2 color (right side / rose by default)",  preview: "20", kind: "highlight" },
      { key: "winner_highlight",     label: "Winner highlight color (frame + score)",           preview: "WIN", kind: "highlight" },
      { key: "winner_by_judge_label",label: "'Winner by judge count' text",                     preview: "WBJC", kind: "text" },
      { key: "judges_panel_bg",      label: "Judges panel background",  preview: null,          kind: "photo" },
      { key: "judge_dot",            label: "Judge dot indicator",      preview: null,          kind: "photo" },
      { key: "judge_name",           label: "Judge name",               preview: "BOYKA",       kind: "text" },
      { key: "judge_status",         label: "Judge status",             preview: "Waiting",     kind: "text" },
    ],
  },
  {
    id: "countdown",
    label: "Countdown Screen (second display)",
    description: "Second display / small screen dedicated to the timer. Only shows the big countdown, the stage label, the round label and (for circles) the circle number.",
    elements: [
      { key: "stage_label",     label: "Stage label (TOP 32 / Circle Q. / Final)", preview: "TOP 32",   kind: "text" },
      { key: "circle_label",    label: "Circle number label",                      preview: "CIRCLE 2", kind: "text" },
      { key: "round_label",     label: "Round label",                              preview: "ROUND 1",  kind: "text" },
      { key: "timer_number",    label: "Timer big number",                         preview: "26",       kind: "text", sizeRange: [0.5, 5] },
    ],
  },
];

/** Sensible defaults per (screenId, elementKey). Missing → generic default. */
export const DEFAULT_STYLE = { color: "#ffffff", size: 1, offset_x: 0, offset_y: 0, shadow: true };

export const DEFAULT_STYLES = {
  circle_start: {
    get_ready_text:       { color: "#ffffff", size: 1, offset_x: 0, offset_y: 0 },
    pre_countdown_number: { color: "#3b82f6", size: 1, offset_x: 0, offset_y: 0 },
  },
  circle_run: {
    circle_title:    { color: "#7dd3fc", size: 1, offset_x: 0, offset_y: 0 },
    round_label:     { color: "#ffffff", size: 1, offset_x: 0, offset_y: 0 },
    timer_number:    { color: "#ffffff", size: 1, offset_x: 0, offset_y: 0 },
    corner_card_bg:  { bg_color: "rgba(0,0,0,0.65)", border_color: "rgba(255,255,255,0.15)", size: 1, offset_x: 0, offset_y: 0 },
    corner_photo:    { bg_color: "#ec4899", size: 1, offset_x: 0, offset_y: 0, color: "#ffffff" },
    corner_name:     { color: "#ffffff", size: 1, offset_x: 0, offset_y: 0 },
    corner_country:  { color: "rgba(255,255,255,0.8)", size: 1, offset_x: 0, offset_y: 0 },
    corner_position: { color: "#fde047", size: 1, offset_x: 0, offset_y: 0 },
    corner_flag:     { size: 1, offset_x: 0, offset_y: 0 },
    active_highlight: { color: "#facc15", glow: 0.55, size: 1, offset_x: 0, offset_y: 0 },
  },
  circle_vote: {
    circle_title:    { color: "#7dd3fc", size: 1, offset_x: 0, offset_y: 0 },
    position_label:  { color: "#a78bfa", size: 1, offset_x: 0, offset_y: 0 },
    athlete_photo:   { bg_color: "#7c3aed", size: 1, offset_x: 0, offset_y: 0, color: "#a78bfa" },
    athlete_flag:    { size: 1, offset_x: 0, offset_y: 0 },
    athlete_name:    { color: "#ffffff", size: 1, offset_x: 0, offset_y: 0 },
    athlete_wr:      { color: "#ef4444", size: 1, offset_x: 0, offset_y: 0 },
    athlete_score:   { color: "#a78bfa", size: 1, offset_x: 0, offset_y: 0 },
    card_background: { bg_color: "rgba(23,10,45,0.85)", border_color: "rgba(255,255,255,0.08)", size: 1, offset_x: 0, offset_y: 0 },
    judges_panel_bg: { bg_color: "rgba(23,10,45,0.85)", border_color: "rgba(255,255,255,0.08)", size: 1, offset_x: 0, offset_y: 0 },
    judge_dot:       { bg_color: "rgba(0,0,0,0)", border_color: "rgba(255,255,255,0.4)", size: 1, offset_x: 0, offset_y: 0 },
    judge_name:      { color: "#ffffff", size: 1, offset_x: 0, offset_y: 0 },
    judge_status:    { color: "rgba(255,255,255,0.6)", size: 1, offset_x: 0, offset_y: 0 },
  },
  battle_run: {
    round_label:  { color: "#ffffff", size: 1, offset_x: 0, offset_y: 0 },
    timer_number: { color: "#ffffff", size: 1, offset_x: 0, offset_y: 0 },
    side_photo:   { bg_color: "#ec4899", size: 1, offset_x: 0, offset_y: 0, color: "#ffffff" },
    side_flag:    { size: 1, offset_x: 0, offset_y: 0 },
    side_name:    { color: "#ffffff", size: 1, offset_x: 0, offset_y: 0 },
    side_country: { color: "rgba(255,255,255,0.85)", size: 1, offset_x: 0, offset_y: 0 },
    side_wr:      { color: "#fbbf24", size: 1, offset_x: 0, offset_y: 0 },
    active_highlight: { color: "#facc15", glow: 0.55, size: 1, offset_x: 0, offset_y: 0 },
  },
  battle_vote: {
    battle_title:    { color: "#7dd3fc", size: 1, offset_x: 0, offset_y: 0 },
    athlete_photo:   { bg_color: "#ec4899", size: 1, offset_x: 0, offset_y: 0, color: "#ffffff" },
    athlete_flag:    { size: 1, offset_x: 0, offset_y: 0 },
    athlete_name:    { color: "#ffffff", size: 1, offset_x: 0, offset_y: 0 },
    athlete_wr:      { color: "#ef4444", size: 1, offset_x: 0, offset_y: 0 },
    vs_label:        { color: "#ffffff", size: 1, offset_x: 0, offset_y: 0 },
    card_background: { bg_color: "rgba(23,10,45,0.85)", border_color: "rgba(255,255,255,0.08)", size: 1, offset_x: 0, offset_y: 0 },
    athlete1_color:  { color: "#a78bfa", glow: 0, size: 1, offset_x: 0, offset_y: 0 },
    athlete2_color:  { color: "#ec4899", glow: 0, size: 1, offset_x: 0, offset_y: 0 },
    winner_highlight:{ color: "#22c55e", glow: 0.55, size: 1, offset_x: 0, offset_y: 0 },
    winner_by_judge_label: { color: "#ffffff", size: 1, offset_x: 0, offset_y: 60 },
    judges_panel_bg: { bg_color: "rgba(23,10,45,0.85)", border_color: "rgba(255,255,255,0.08)", size: 1, offset_x: 0, offset_y: 0 },
    judge_dot:       { bg_color: "rgba(0,0,0,0)", border_color: "rgba(255,255,255,0.4)", size: 1, offset_x: 0, offset_y: 0 },
    judge_name:      { color: "#ffffff", size: 1, offset_x: 0, offset_y: 0 },
    judge_status:    { color: "rgba(255,255,255,0.6)", size: 1, offset_x: 0, offset_y: 0 },
  },
  countdown: {
    stage_label:   { color: "#7dd3fc", size: 1, offset_x: 0, offset_y: 0 },
    circle_label:  { color: "#a78bfa", size: 1, offset_x: 0, offset_y: 0 },
    round_label:   { color: "#ffffff", size: 1, offset_x: 0, offset_y: 0 },
    timer_number:  { color: "#ffffff", size: 1, offset_x: 0, offset_y: 0 },
  },
};
